/**
 * Loop pacing for the films. A film is a pure function of its progress p
 * (0..1); a pace lays p out in time. `knots` are [p, ms] pairs: each
 * segment ends at that p and lasts that long, so strokes, dwells and holds
 * each get their own duration without touching the film's timings.
 */

/**
 * @param {Array<[number, number]>} knots ascending p, ending at 1
 * @returns {{ duration: number, filmP: (q: number) => number, loopQ: (p: number) => number }}
 */
export function makePace(knots) {
  const ends = knots.reduce((acc, [p, ms]) => [...acc, { p, t: (acc.at(-1)?.t ?? 0) + ms }], [])
  const duration = ends.at(-1).t
  const points = [{ p: 0, t: 0 }, ...ends]

  /** Film progress at loop position q (0..1). */
  const filmP = (q) => {
    const t = q * duration
    const i = points.findIndex((point) => point.t >= t)
    if (i <= 0) return i === 0 ? 0 : 1
    const a = points[i - 1]
    const b = points[i]
    return a.p + ((b.p - a.p) * (t - a.t)) / (b.t - a.t)
  }

  /** Loop position q (0..1) at film progress p. */
  const loopQ = (p) => {
    const i = points.findIndex((point) => point.p >= p)
    if (i <= 0) return i === 0 ? 0 : 1
    const a = points[i - 1]
    const b = points[i]
    return (a.t + ((b.t - a.t) * (p - a.p)) / (b.p - a.p)) / duration
  }

  return { duration, filmP, loopQ }
}

/**
 * The cycle ruler is split into equal thirds, one per station, whatever
 * share of the film a station takes: position on the ruler at film progress p.
 *
 * @param {number} p
 * @param {number[]} stations start of each station, the first at 0
 */
export function rulerAt(p, stations) {
  const i = stations.reduce((live, start, k) => (p >= start ? k : live), 0)
  const from = stations[i]
  const to = stations[i + 1] ?? 1
  return (i + Math.min(1, (p - from) / (to - from))) / stations.length
}

/* In the last 3 % of a loop the ruler's fill runs out to the right, so the
   next cycle starts on an empty line instead of a jump back. */
const DRAIN_FROM = 0.97

/** [start, end] of the ruler fill, 0..1 of the line. */
export const rulerFill = (q, p, stations) => [
  Math.max(0, (q - DRAIN_FROM) / (1 - DRAIN_FROM)),
  rulerAt(p, stations),
]
